import React, { useState, useEffect, useRef, useMemo, lazy, Suspense } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { addUnit, normalizeAppUrl } from '../utils/styleHelpers';
import { slugify, getProductImageUrl, getProductDisplayPrice } from '../utils/helpers';
import DraggableToolbar from './DraggableToolbar';
import { LuSettings, LuX } from 'react-icons/lu';

const SearchCustomizer = lazy(() => import('./dashboard/SearchCustomizer'));

/**
 * InlineSearchBar — Basic search bar embedded in site header/pages.
 * Provides direct, embedded search input without popping up a modal/overlay dialog.
 */
export const InlineSearchBar = ({
    config = {},
    onSetConfig = config.onSetConfig,
    onSaveConfig = config.onSaveConfig,
    placeholder,
    className = '',
    style = {},
    onSelectProduct,
    compact = false,
    showEditButton = true,
    isEditing = false
}) => {
    const settings = config.settings || {};
    const navigate = useNavigate();
    const effectiveIsEditing = isEditing || config.isEditing || false;
    const [isCustomizerOpen, setIsCustomizerOpen] = useState(false);

    // Comprehensive CSS Customization Tokens from config
    const accentColor = settings.searchAccentColor || settings.primaryColor || '#3b82f6';
    const containerBgColor = settings.searchContainerBgColor || settings.searchBgColor || 'rgba(255, 255, 255, 0.12)';
    const inputBgColor = settings.searchInputBgColor || 'transparent';
    const textColor = settings.searchTextColor || '#ffffff';
    const borderColor = settings.searchContainerBorderColor || settings.searchBorderColor || 'rgba(255, 255, 255, 0.2)';
    const borderWidth = addUnit(settings.searchContainerBorderWidth || settings.searchBorderWidth || '1px');
    const borderStyleType = settings.searchContainerBorderStyle || settings.searchBorderStyle || 'solid';
    const borderRadius = addUnit(settings.searchContainerRadius || settings.searchRadius || '24px');
    const fontSize = addUnit(settings.searchFontSize || (compact ? '0.85rem' : '0.95rem'));
    const fontWeight = settings.searchFontWeight || 'normal';
    const fontFamily = settings.searchFontFamily || 'inherit';
    const padding = addUnit(settings.searchPadding || (compact ? '6px 14px' : '8px 18px'));
    const shadow = settings.searchContainerShadow || settings.searchShadow || 'none';
    const placeholderText = placeholder || settings.searchPlaceholder || 'Search products...';
    const placeholderColor = settings.searchPlaceholderColor || 'rgba(255, 255, 255, 0.55)';
    const resultsBgColor = settings.searchResultsBgColor || settings.searchBgColor || '#0f172a';
    const resultsTextColor = settings.searchResultsTextColor || textColor;

    const customWidth = settings.searchContainerWidth || settings.searchWidth;
    const defaultWidth = compact ? '220px' : '320px';
    const computedWidth = customWidth ? addUnit(customWidth) : (style?.width || defaultWidth);

    const [query, setQuery] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const containerRef = useRef(null);

    const products = useMemo(() => {
        if (config.products && config.products.length > 0) return config.products;
        return [
            { id: 'p-1', name: 'Omnivaso Fine Art Print', category: 'Prints', price: '$49.00', description: 'High resolution giclée print on textured cotton paper.' },
            { id: 'p-2', name: 'Modern Abstract Canvas', category: 'Canvas', price: '$120.00', description: 'Original gallery-wrapped canvas with vivid acrylic tones.' },
            { id: 'p-3', name: 'Handcrafted Ceramic Vessel', category: 'Decor', price: '$85.00', description: 'Minimalist stoneware vessel with smooth matte glaze.' },
            { id: 'p-4', name: 'Digital Art Showcase Asset', category: 'Digital', price: '$29.00', description: 'Curated 4K digital artwork asset bundle.' }
        ];
    }, [config.products]);

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return [];
        return products.filter(p =>
            p.name?.toLowerCase().includes(q) ||
            p.category?.toLowerCase().includes(q) ||
            p.description?.toLowerCase().includes(q)
        ).slice(0, 8);
    }, [products, query]);

    useEffect(() => setSelectedIndex(0), [results]);

    // Listen for global customizer open events
    useEffect(() => {
        const handleOpenCustomizer = () => setIsCustomizerOpen(true);
        window.addEventListener('open-search-customizer-modal', handleOpenCustomizer);
        return () => window.removeEventListener('open-search-customizer-modal', handleOpenCustomizer);
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsFocused(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (product) => {
        setQuery('');
        setIsFocused(false);
        if (onSelectProduct) onSelectProduct(product);
        else navigate(normalizeAppUrl(`/product/${product.slug || product.id}`));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(i => Math.min(i + 1, results.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter' && results[selectedIndex]) {
            e.preventDefault();
            const product = results[selectedIndex];
            setQuery('');
            setIsFocused(false);
            navigate(normalizeAppUrl(`/product/${product.slug || product.id}`));
        } else if (e.key === 'Escape') {
            setIsFocused(false);
        }
    };

    const highlight = (text = '', q) => {
        if (!q.trim()) return text;
        const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
        return parts.map((part, i) =>
            part.toLowerCase() === q.toLowerCase()
                ? <mark key={i} style={{ background: accentColor, color: '#fff', borderRadius: '2px', padding: '0 2px' }}>{part}</mark>
                : part
        );
    };

    const showDropdown = isFocused && query.trim().length > 0;

    return (
        <div
            ref={containerRef}
            className={`inline-search-bar ${className}`}
            style={{
                position: 'relative',
                maxWidth: '100%',
                ...style,
                width: computedWidth
            }}
        >
            <style>{`
                .inline-search-bar input::placeholder {
                    color: ${placeholderColor};
                    opacity: 1;
                }
            `}</style>

            {/* Floating Admin Live Customizer Trigger Button */}
            {effectiveIsEditing && showEditButton && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setIsCustomizerOpen(true);
                        window.dispatchEvent(new CustomEvent('open-search-customizer-modal'));
                    }}
                    title="Customize Search Bar CSS & Layout Live"
                    style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        color: '#ffffff',
                        border: '2px solid #ffffff',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        cursor: 'pointer',
                        fontSize: '0.72rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 20,
                        boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                    }}
                    onMouseOver={e => {
                        e.currentTarget.style.transform = 'scale(1.2) rotate(30deg)';
                        e.currentTarget.style.boxShadow = '0 6px 14px rgba(59, 130, 246, 0.5)';
                    }}
                    onMouseOut={e => {
                        e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                        e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.3)';
                    }}
                >
                    <LuSettings size={12} />
                </button>
            )}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: isFocused ? (settings.searchHoverBgColor || containerBgColor) : containerBgColor,
                border: `${borderWidth} ${borderStyleType} ${borderColor}`,
                borderRadius: borderRadius,
                padding: padding,
                boxShadow: shadow,
                transition: 'all 0.2s ease',
                backdropFilter: settings.searchGlassmorphic !== false ? `blur(${settings.searchBackdropBlur || '8px'})` : 'none'
            }}>
                <span style={{ fontSize: compact ? '0.85rem' : '1rem', opacity: 0.7, color: textColor }}>🔍</span>
                <input
                    type="text"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholderText}
                    style={{
                        flex: 1,
                        background: inputBgColor,
                        border: 'none',
                        outline: 'none',
                        color: textColor,
                        fontSize: fontSize,
                        fontWeight: fontWeight,
                        fontFamily: fontFamily,
                        minWidth: 0
                    }}
                />
                {query && (
                    <button
                        type="button"
                        onClick={() => setQuery('')}
                        style={{
                            background: 'rgba(255,255,255,0.15)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '18px', height: '18px',
                            cursor: 'pointer',
                            color: textColor,
                            fontSize: '0.65rem',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0
                        }}
                    ><LuX size={10} /></button>
                )}
            </div>

            {/* Inline Results Dropdown */}
            {showDropdown && (
                <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 6px)',
                    left: 0, right: 0,
                    zIndex: 2500,
                    background: resultsBgColor,
                    color: resultsTextColor,
                    border: `${borderWidth} ${borderStyleType} ${settings.searchResultsBorderColor || borderColor}`,
                    borderRadius: addUnit(settings.searchResultsRadius || settings.searchContainerRadius || settings.searchRadius || '12px'),
                    boxShadow: settings.searchResultsShadow || '0 12px 30px rgba(0, 0, 0, 0.35)',
                    maxHeight: addUnit(settings.searchResultsMaxHeight || '340px'),
                    overflowY: 'auto',
                    padding: '6px',
                    animation: 'srch-slide-down 0.15s ease-out'
                }}>
                    <style>{`
                        @keyframes srch-slide-down {
                            from { opacity: 0; transform: translateY(-4px); }
                            to { opacity: 1; transform: translateY(0); }
                        }
                    `}</style>
                    {results.length === 0 ? (
                        <div style={{ padding: '16px', textAlign: 'center', opacity: 0.6, fontSize: '0.85rem' }}>
                            No products found for "{query}"
                        </div>
                    ) : (
                        results.map((p, idx) => {
                            const isSelected = idx === selectedIndex;
                            const image = getProductImageUrl(p);
                            return (
                                <div
                                    key={p.id}
                                    onClick={() => handleSelect(p)}
                                    onMouseEnter={() => setSelectedIndex(idx)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '8px 10px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        background: isSelected ? `${accentColor}25` : 'transparent',
                                        border: isSelected ? `1px solid ${accentColor}55` : '1px solid transparent',
                                        transition: 'background 0.12s, border 0.12s'
                                    }}
                                >
                                    <div style={{
                                        width: '34px', height: '34px', flexShrink: 0,
                                        borderRadius: '6px', overflow: 'hidden',
                                        background: 'rgba(255,255,255,0.08)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        {image
                                            ? <img src={image} onError={e => { e.target.onerror = null; e.target.src = '/hero_abstract_bg.png'; }} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            : <span style={{ fontSize: '0.9rem' }}>🛍️</span>
                                        }
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            fontWeight: '600', fontSize: '0.85rem',
                                            color: resultsTextColor,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                        }}>
                                            {highlight(p.name, query)}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '1px' }}>
                                            {p.category && <span>{p.category}</span>}
                                            <span style={{ color: accentColor, fontWeight: 'bold', marginLeft: '6px' }}>
                                                {getProductDisplayPrice(p)}
                                            </span>
                                        </div>
                                    </div>
                                    <span style={{ fontSize: '0.75rem', color: isSelected ? accentColor : 'rgba(255,255,255,0.2)' }}>→</span>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            <SearchCustomizerModal
                isOpen={isCustomizerOpen}
                onClose={() => setIsCustomizerOpen(false)}
                config={config}
                onSetConfig={onSetConfig || config?.onSetConfig}
                onSaveConfig={onSaveConfig || config?.onSaveConfig}
            />
        </div>
    );
};

/**
 * SearchTool Component
 * Supports multiple layout modes: 'modal' (spotlight), 'drawer', 'fullscreen', 'grid', 'inline'.
 */
export const SearchTool = ({
    config = {},
    isOpen = false,
    onClose = () => {},
    onSetConfig,
    onSaveConfig = () => {},
    isEmbedded = false
}) => {
    const settings = config.settings || {};
    const navigate = useNavigate();

    const [isCustomizerModalOpen, setIsCustomizerModalOpen] = useState(false);

    useEffect(() => {
        if (isEmbedded) return;
        const handleOpenCustomizer = () => setIsCustomizerModalOpen(true);
        window.addEventListener('open-search-customizer-modal', handleOpenCustomizer);
        return () => window.removeEventListener('open-search-customizer-modal', handleOpenCustomizer);
    }, [isEmbedded]);

    const layout = settings.searchLayout || 'modal';

    // Comprehensive CSS Customization Tokens
    const accentColor = settings.searchAccentColor || settings.primaryColor || '#3b82f6';
    const containerBgColor = settings.searchContainerBgColor || settings.searchBgColor || '#0f172a';
    const textColor = settings.searchTextColor || '#f8fafc';
    const borderColor = settings.searchContainerBorderColor || settings.searchBorderColor || 'rgba(255, 255, 255, 0.15)';
    const borderWidth = addUnit(settings.searchContainerBorderWidth || '1px');
    const borderStyleType = settings.searchContainerBorderStyle || 'solid';
    const borderRadius = addUnit(settings.searchContainerRadius || settings.searchRadius || '14px');
    const containerShadow = settings.searchContainerShadow || settings.searchShadow || '0 25px 50px rgba(0,0,0,0.6)';
    const containerWidth = addUnit(settings.searchContainerWidth || settings.searchWidth || '640px');
    const placeholderColor = settings.searchPlaceholderColor || 'rgba(255, 255, 255, 0.55)';

    const [query, setQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef(null);

    // Auto-focus when opened
    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 80);
        }
        if (!isOpen) setQuery('');
    }, [isOpen]);

    // Keyboard navigation (ESC to close + Cmd/Ctrl+K shortcut)
    useEffect(() => {
        const handleKey = (e) => {
            if (isOpen && e.key === 'Escape') onClose();
            if (settings.searchShortcutEnabled === true && (e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                if (layout !== 'inline') {
                    isOpen ? onClose() : window.dispatchEvent(new CustomEvent('open-search-tool'));
                }
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [isOpen, onClose, settings.searchShortcutEnabled, layout]);

    const products = useMemo(() => config.products || [], [config.products]);

    const categories = useMemo(() => {
        const set = new Set(['All']);
        if (Array.isArray(config.categories) && config.categories.length > 0) {
            config.categories.filter(c => !c.hidden && !c.parentId).forEach(c => set.add(c.name));
        } else {
            products.forEach(p => { if (p.category) set.add(p.category); });
        }
        return Array.from(set);
    }, [products, config.categories]);

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = products;
        if (selectedCategory !== 'All') {
            const target = selectedCategory.trim().toLowerCase();
            list = list.filter(p => {
                if (!p.category) return false;
                const cat = p.category.trim().toLowerCase();
                return cat === target || slugify(cat) === slugify(target);
            });
        }
        if (!q) return list.slice(0, 16);
        return list.filter(p =>
            p.name?.toLowerCase().includes(q) ||
            p.category?.toLowerCase().includes(q) ||
            p.description?.toLowerCase().includes(q)
        ).slice(0, 16);
    }, [products, query, selectedCategory]);

    useEffect(() => setSelectedIndex(0), [results]);

    const handleSelect = (product) => {
        onClose();
        navigate(normalizeAppUrl(`/product/${product.slug || product.id}`));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(i => Math.min(i + 1, results.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter' && results[selectedIndex]) {
            e.preventDefault();
            handleSelect(results[selectedIndex]);
        }
    };

    const highlight = (text = '', q) => {
        if (!q.trim()) return text;
        const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
        return parts.map((part, i) =>
            part.toLowerCase() === q.toLowerCase()
                ? <mark key={i} style={{ background: accentColor, color: '#fff', borderRadius: '2px', padding: '0 2px' }}>{part}</mark>
                : part
        );
    };

    const q = query.trim().toLowerCase();

    // Body content renderer for modal/drawer/embedded
    const bodyContent = (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: isEmbedded ? '600px' : '75vh' }}>
            <style>{`
                .srch-modal-input::placeholder {
                    color: ${placeholderColor} !important;
                    opacity: 1 !important;
                }
            `}</style>
            {/* Header Search Input Bar */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '16px 20px',
                borderBottom: `${borderWidth} ${borderStyleType} ${borderColor}`
            }}>
                <span style={{ fontSize: '1.2rem', opacity: 0.75, color: textColor }}>🔍</span>
                <input
                    ref={inputRef}
                    type="text"
                    className="srch-modal-input"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={settings.searchPlaceholder || 'Search products by name, category, keyword...'}
                    style={{
                        flex: 1,
                        background: settings.searchInputBgColor || 'transparent',
                        border: 'none',
                        outline: 'none',
                        color: textColor,
                        fontSize: addUnit(settings.searchFontSize || '1.05rem'),
                        fontWeight: settings.searchFontWeight || 'normal',
                        fontFamily: settings.searchFontFamily || 'inherit'
                    }}
                />
                {query && (
                    <button
                        type="button"
                        onClick={() => setQuery('')}
                        style={{
                            background: 'rgba(255,255,255,0.12)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px', height: '24px',
                            cursor: 'pointer',
                            color: textColor,
                            fontSize: '0.75rem',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                    ><LuX size={12} /></button>
                )}
                {!isEmbedded && (
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: textColor,
                            opacity: 0.6,
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            marginLeft: '8px'
                        }}
                    >ESC</button>
                )}
            </div>

            {/* Category Filter Pills */}
            {categories.length > 1 && (
                <div style={{
                    display: 'flex', gap: '8px', padding: '10px 20px',
                    borderBottom: `1px solid ${borderColor}`,
                    overflowX: 'auto'
                }}>
                    {categories.map(cat => (
                        <button
                            key={cat}
                            type="button"
                            onClick={() => setSelectedCategory(cat)}
                            style={{
                                background: selectedCategory === cat ? accentColor : 'rgba(255,255,255,0.08)',
                                color: selectedCategory === cat ? '#ffffff' : textColor,
                                border: 'none',
                                borderRadius: '20px',
                                padding: '4px 12px',
                                fontSize: '0.8rem',
                                fontWeight: selectedCategory === cat ? 'bold' : 'normal',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.15s ease'
                            }}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            )}

            {/* Results Area */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '12px' }}>
                {!q && selectedCategory === 'All' && products.length === 0 ? (
                    <div style={{ padding: '40px 20px', textAlign: 'center', opacity: 0.5, fontSize: '0.95rem' }}>
                        No products loaded in store.
                    </div>
                ) : results.length === 0 ? (
                    <div style={{ padding: '40px 20px', textAlign: 'center', opacity: 0.5, fontSize: '0.95rem' }}>
                        No products found for <strong style={{ color: textColor }}>"{query}"</strong>
                    </div>
                ) : layout === 'grid' ? (
                    /* Card Grid View Layout */
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                        gap: '12px',
                        padding: '8px'
                    }}>
                        {results.map((p, idx) => {
                            const isSelected = idx === selectedIndex;
                            const image = getProductImageUrl(p);
                            return (
                                <div
                                    key={p.id}
                                    onClick={() => handleSelect(p)}
                                    onMouseEnter={() => setSelectedIndex(idx)}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px',
                                        padding: '10px',
                                        borderRadius: '10px',
                                        cursor: 'pointer',
                                        background: isSelected ? `${accentColor}25` : 'transparent',
                                        border: isSelected ? `1px solid ${accentColor}55` : '1px solid transparent',
                                        transition: 'background 0.12s, border 0.12s'
                                    }}
                                >
                                    <div style={{
                                        width: '100%', height: '110px',
                                        borderRadius: '8px', overflow: 'hidden',
                                        background: 'rgba(255,255,255,0.08)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        {image
                                            ? <img src={image} onError={e => { e.target.onerror = null; e.target.src = '/hero_abstract_bg.png'; }} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            : <span style={{ fontSize: '2rem' }}>🛍️</span>
                                        }
                                    </div>
                                    <div style={{ fontWeight: 'bold', fontSize: '0.88rem', color: textColor }}>
                                        {highlight(p.name, q)}
                                    </div>
                                    <div style={{ fontSize: '0.78rem', color: accentColor, fontWeight: 'bold' }}>
                                        {getProductDisplayPrice(p)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    /* Standard List View */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {results.map((p, idx) => {
                            const isSelected = idx === selectedIndex;
                            const image = getProductImageUrl(p);
                            return (
                                <div
                                    key={p.id}
                                    onClick={() => handleSelect(p)}
                                    onMouseEnter={() => setSelectedIndex(idx)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '10px 14px',
                                        borderRadius: '10px',
                                        cursor: 'pointer',
                                        background: isSelected ? `${accentColor}25` : 'transparent',
                                        border: isSelected ? `1px solid ${accentColor}55` : '1px solid transparent',
                                        transition: 'background 0.12s, border 0.12s',
                                        marginBottom: '4px'
                                    }}
                                >
                                    <div style={{
                                        width: '42px', height: '42px', flexShrink: 0,
                                        borderRadius: '8px', overflow: 'hidden',
                                        background: 'rgba(255,255,255,0.08)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        {image
                                            ? <img src={image} onError={e => { e.target.onerror = null; e.target.src = '/hero_abstract_bg.png'; }} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            : <span style={{ fontSize: '1rem' }}>🛍️</span>
                                        }
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            fontWeight: '600', fontSize: '0.92rem',
                                            color: textColor,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                        }}>
                                            {highlight(p.name, q)}
                                        </div>
                                        <div style={{ fontSize: '0.78rem', opacity: 0.6, marginTop: '2px' }}>
                                            {p.category && <span>{p.category}</span>}
                                            {p.price != null && (
                                                <span style={{ color: accentColor, fontWeight: 'bold', marginLeft: '6px' }}>
                                                    ${Number(p.price).toFixed(2)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <span style={{ fontSize: '0.85rem', color: isSelected ? accentColor : 'rgba(255,255,255,0.2)' }}>→</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );

    // Embedded Mode
    if (isEmbedded) {
        return (
            <div style={{
                background: containerBgColor,
                color: textColor,
                borderRadius: borderRadius,
                border: `${borderWidth} ${borderStyleType} ${borderColor}`,
                boxShadow: containerShadow,
                overflow: 'hidden'
            }}>
                {bodyContent}
            </div>
        );
    }

    // DO NOT render popup modal overlay if search layout is set to 'inline' or if modal is not open
    if (!isOpen || layout === 'inline') return null;

    // Sidebar Drawer Layout
    if (layout === 'drawer') {
        const side = settings.searchDrawerSide === 'left' ? 'left' : 'right';
        return (
            <div
                onClick={onClose}
                style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    background: settings.searchBackdropBg || 'rgba(0,0,0,0.6)',
                    backdropFilter: settings.searchGlassmorphic !== false ? `blur(${settings.searchBackdropBlur || '8px'})` : 'none',
                    display: 'flex', justifyContent: side === 'left' ? 'flex-start' : 'flex-end'
                }}
            >
                <div
                    onClick={e => e.stopPropagation()}
                    style={{
                        width: containerWidth, maxWidth: '90vw', height: '100%',
                        background: containerBgColor,
                        color: textColor,
                        borderLeft: side === 'right' ? `${borderWidth} ${borderStyleType} ${borderColor}` : 'none',
                        borderRight: side === 'left' ? `${borderWidth} ${borderStyleType} ${borderColor}` : 'none',
                        boxShadow: containerShadow,
                        animation: side === 'left' ? 'srch-slide-left 0.25s ease-out' : 'srch-slide-right 0.25s ease-out'
                    }}
                >
                    <style>{`
                        @keyframes srch-slide-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
                        @keyframes srch-slide-left { from { transform: translateX(-100%); } to { transform: translateX(0); } }
                    `}</style>
                    {bodyContent}
                </div>
            </div>
        );
    }

    const customizerModalMarkup = (
        <SearchCustomizerModal
            isOpen={isCustomizerModalOpen}
            onClose={() => setIsCustomizerModalOpen(false)}
            config={config}
            onSetConfig={onSetConfig}
            onSaveConfig={onSaveConfig}
        />
    );

    if (!isOpen && !isEmbedded) {
        return customizerModalMarkup;
    }

    if (layout === 'inline' && !isEmbedded) {
        return customizerModalMarkup;
    }

    // Fullscreen Overlay Layout
    if (layout === 'fullscreen') {
        return (
            <>
                {customizerModalMarkup}
                <div
                    onClick={onClose}
                    style={{
                        position: 'fixed', inset: 0, zIndex: 9999,
                        background: containerBgColor,
                        color: textColor,
                        backdropFilter: settings.searchGlassmorphic !== false ? `blur(${settings.searchBackdropBlur || '16px'})` : 'none',
                        padding: settings.searchContainerPadding || '40px 20px',
                        display: 'flex', justifyContent: 'center', alignItems: 'flex-start'
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{ width: containerWidth, maxWidth: '100%', height: '100%' }}
                    >
                        {bodyContent}
                    </div>
                </div>
            </>
        );
    }

    // Standard Spotlight Modal Layout
    return (
        <>
            {customizerModalMarkup}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
                    padding: settings.searchContainerPadding || '80px 20px 20px',
                    background: settings.searchBackdropBg || 'rgba(0,0,0,0.65)',
                    backdropFilter: settings.searchGlassmorphic !== false ? `blur(${settings.searchBackdropBlur || '8px'})` : 'none',
                    animation: 'srch-fadein 0.2s ease-out'
                }}
            >
                <style>{`
                    @keyframes srch-fadein { from { opacity: 0; } to { opacity: 1; } }
                    @keyframes srch-pop { from { opacity: 0; transform: scale(0.96) translateY(-8px); } to { opacity: 1; transform: scale(1) translateY(0); } }
                `}</style>
                <div
                    onClick={e => e.stopPropagation()}
                    style={{
                        width: containerWidth, maxWidth: '100%',
                        background: containerBgColor,
                        color: textColor,
                        borderRadius: borderRadius,
                        border: `${borderWidth} ${borderStyleType} ${borderColor}`,
                        boxShadow: containerShadow,
                        overflow: 'hidden',
                        animation: 'srch-pop 0.22s cubic-bezier(0.16,1,0.3,1)'
                    }}
                >
                    {bodyContent}
                </div>
            </div>
        </>
    );
};

/**
 * Global Portal Modal for live customizing Search Tool CSS & layout.
 */
export const SearchCustomizerModal = ({ isOpen, onClose, config, onSetConfig }) => {
    if (!isOpen || typeof document === 'undefined') return null;

    const handleModalClose = () => {
        if (onClose) onClose();
    };

    return (
        <DraggableToolbar title="🔍 Customize Search Bar CSS & Layout Live" onClose={handleModalClose}>
            <div
                style={{
                    width: '360px',
                    maxWidth: '90vw',
                    maxHeight: '75vh',
                    overflowY: 'auto',
                    padding: '8px',
                    fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
                }}
                onClick={e => e.stopPropagation()}
            >
                <Suspense fallback={<div style={{ padding: '16px', color: '#64748b', fontSize: '0.85rem' }}>Loading Search Customizer...</div>}>
                    <SearchCustomizer
                        config={config}
                        isLiveDrawer={true}
                        InlineSearchBarComponent={InlineSearchBar}
                        SearchToolComponent={SearchTool}
                        onSave={(newData) => {
                            // LIVE PREVIEW: Update page draft state in real time as user edits
                            if (onSetConfig) onSetConfig(newData);
                        }}
                        onClose={handleModalClose}
                    />
                </Suspense>
            </div>
        </DraggableToolbar>
    );
};

export default SearchTool;

